import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import Select from 'components/select';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { OrderPayload, showisExistADGrp, selectedMembersList, memberForm, costEstimateState } from 'pages/myidm/store';
import { resetCostEstimateDetails } from 'pages/myidm/utils';
import useDebounce from 'pages/myidm/components/hooks/use-debounce';
import { InputInfo } from 'pages/myidm/components/input-info';
import { REQUEST_TYPE } from 'pages/myidm/utils/constant';

const MultipleADGroup = ({ index = 0 }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [, setIsExistADGrp] = useRecoilState(showisExistADGrp);
    const [, setMembers] = useRecoilState(selectedMembersList);
    const [form, setForm] = useRecoilState(memberForm);
    const [options, setOptions] = React.useState([]);
    const [inputValue, setInputValue] = React.useState('');
    const [notFound, setNotFound] = React.useState(false);
    const [, setcostEstimate] = useRecoilState(costEstimateState);

    const adGroupsApi = useMutation((searchVal) => axios.get(`${endPoints?.myIdm?.adGroupSearchApi(searchVal, order?.domainType)}`));
    const adGroupDetailsApi = useMutation((adGrp) =>
        axios.get(
            `${endPoints.myIdm.adGroupDetailsApi}/${adGrp}${
                order?.domainType?.toLowerCase().startsWith('cww') ? `?domain=${encodeURIComponent(order.domainType)}` : ''
            }`
        )
    );
    const adGrpUniversalApi = useMutation((adGrp) => axios.get(`${endPoints?.myIdm?.adGroupIsUniversalApi}/${adGrp}`));

    const debouncedInputValue = useDebounce(inputValue, 500);
    const setAdGroup = async (adGroup) => {
        // Call adGroupDetailsApi and update with adGroupOwner
        let adGroupOwner = '';
        let adGroupType = '';
        let isUniversalADGroup = false;
        try {
            const response = await adGroupDetailsApi.mutateAsync(adGroup);
            const adGroupData = response?.data?.[0] || {};
            adGroupOwner =
                adGroupData.adgroupOwnerId && adGroupData.adgroupOwnerName ? { gpid: adGroupData.adgroupOwnerId, name: adGroupData.adgroupOwnerName } : '';
            adGroupType = adGroupData?.adgroupType || '';
        } catch (e) {
            adGroupOwner = '';
        }
        try {
            const universalResponse = await adGrpUniversalApi.mutateAsync(adGroup);
            isUniversalADGroup = universalResponse?.data?.isUniversalGroup || false;
        } catch (e) {
            isUniversalADGroup = false;
        }
        const updatedMultipleAdGroups = [...(order?.multipleAdGroups || [])];
        updatedMultipleAdGroups[index] = { adGroup, adGroupOwner, adGroupType, isUniversalADGroup };
        setOrder({
            ...order,
            multipleAdGroups: updatedMultipleAdGroups,
        });
        setForm({
            ...form,
            selectedMember: null,
            member: null,
        });
        setMembers([]);
        setcostEstimate(resetCostEstimateDetails);
    };
    // React.useEffect(() => {
    //     if (adGroupDetailsApi?.data?.data) {
    //         const adGroupData = adGroupDetailsApi?.data?.data?.[0] || {};
    //         setOrder({
    //             ...order,
    //             adGroupType: adGroupDetailsApi?.data?.data?.[0]?.adgroupType,
    //             adGroupTypeDetails: null,
    //             groupOwner: null,
    //             groupOwnerDetails: null,
    //             estimatedCost: resetCostEstimateDetails,
    //             adGroupOwner:
    //                 adGroupData.adgroupOwnerId && adGroupData.adgroupOwnerName ? { gpid: adGroupData.adgroupOwnerId, name: adGroupData.adgroupOwnerName } : '',
    //         });
    //     }
    // }, [adGroupDetailsApi?.isSuccess]);
    const handleOnSelectChange = (selected) => {
        setIsExistADGrp(false);
        setNotFound(false);
        setAdGroup(selected.value);
    };

    React.useEffect(() => {
        // Only fetch details for the primary AD group (index 0)
        if (index === 0 && order?.typeOfRequest === REQUEST_TYPE.MODIFY && !order?.adGroupType && order?.adGroup) adGroupDetailsApi?.mutate(order?.adGroup);
        // For additional AD groups, we don't need to fetch details currently
    }, [order?.typeOfRequest, order?.adGroupType, order?.adGroup, order?.multipleAdGroups]);

    React.useEffect(() => {
        if (adGroupsApi?.isSuccess && adGroupsApi.data?.data && debouncedInputValue !== '') {
            // Filter out already selected adGroups in multipleAdGroups (except current index)
            const selectedAdGroups = (order?.multipleAdGroups || [])
                .filter((_, i) => i !== index)
                .map((g) => g?.adGroup)
                .filter(Boolean);
            const adList = adGroupsApi.data?.data
                ?.filter((adGroup) => !selectedAdGroups.includes(adGroup))
                .map((adGroup) => ({
                    label: adGroup,
                    value: adGroup,
                }));
            setIsExistADGrp(false);
            setNotFound(false);
            setOptions(adList);
        } else if (
            adGroupsApi?.isSuccess &&
            adGroupsApi?.data?.data === null &&
            adGroupsApi?.data?.messages?.includes('No Data Found') &&
            debouncedInputValue !== ''
        ) {
            setOrder({ ...order, crossDomainUser: null, isUniversalADGroup: false, members: null, adGroupMembers: null });
            setMembers([]);
            setForm({
                ...form,
                selectedMember: null,
                member: null,
            });
            setNotFound(true);
            setIsExistADGrp(true);
        }
    }, [adGroupsApi?.isSuccess]);

    useEffect(() => {
        if (debouncedInputValue) {
            setOptions([]);
            adGroupsApi.mutate(debouncedInputValue);
        } else {
            setOptions([]);
        }
    }, [debouncedInputValue]);
    React.useEffect(() => {
        if (!order?.adGroup && order?.isTouched && index === 0) setIsTouched(true);
        else if (index > 0 && order?.multipleAdGroups && !order?.multipleAdGroups[index - 1] && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.adGroup, order?.isTouched, order?.multipleAdGroups, index]);

    // Get the current value based on index
    const getCurrentValue = () => {
        if (order?.multipleAdGroups && order?.multipleAdGroups.length > index) {
            return order?.multipleAdGroups[index]?.adGroup || null;
        }
        return null;
    };
    const isAnyLoading = adGroupsApi.isLoading || adGroupDetailsApi.isLoading || adGrpUniversalApi.isLoading;
    return (
        <>
            <Box display="flex" alignItems="center" gap={1}>
                <Select
                    key={`adGroup-${index}`}
                    label="Active Directory Group *"
                    value={getCurrentValue()}
                    options={options}
                    disabled={!order.businessApplication || !order?.domainType}
                    handleOnSelect={(value) => handleOnSelectChange(value)}
                    isLoading={isAnyLoading}
                    noOptionsText="Type to search"
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    fullWidth
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    isRequired={isTouched}
                    sx={{ mt: 0, mb: 0 }}
                    customValidate={notFound ? 'validate' : ''}
                    dataTestId={`adgrp-auto-${index}`}
                    onKeyUp={(event) => {
                        if (event.key === 'Backspace' || event.key === 'Delete') {
                            const updatedMultipleAdGroups = [...(order?.multipleAdGroups || [])];
                            if (index < updatedMultipleAdGroups.length) {
                                updatedMultipleAdGroups[index] = { adGroup: null };
                                setOrder({ ...order, multipleAdGroups: updatedMultipleAdGroups });
                            }
                            setForm({
                                ...form,
                                selectedMember: null,
                                member: null,
                            });
                            setMembers([]);
                        }
                    }}
                    onBlur={() => {
                        if (options?.length === 0) {
                            const updatedMultipleAdGroups = [...(order?.multipleAdGroups || [])];
                            if (index < updatedMultipleAdGroups.length) {
                                updatedMultipleAdGroups[index] = { adGroup: null };
                                setOrder({ ...order, multipleAdGroups: updatedMultipleAdGroups });
                            }
                            setNotFound(false);
                        }
                    }}
                />
                <InputInfo title="Type characters for suggestions." />
            </Box>
        </>
    );
};

MultipleADGroup.propTypes = {
    index: PropTypes.number,
};

MultipleADGroup.defaultProps = {
    index: 0,
};

export default React.memo(MultipleADGroup);
